/**
 * @file SiteSettingsContext.js
 * @brief Fetches and provides the platform StaffSettings singleton to the React component tree.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const SiteSettingsContext = createContext({
    siteSettings: null,
    loading: true,
    refreshSiteSettings: () => {},
});

const API_BASE = process.env.REACT_APP_API_BASE_URL + "/api/v1";

/**
 * Fetch the platform settings once on mount and expose them through context.
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children Descendant elements that consume site settings.
 * @returns {JSX.Element} Context provider wrapping the application subtree.
 */
export function SiteSettingsProvider({ children }) {
    const [siteSettings, setSiteSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshSiteSettings = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/settings/`);
            setSiteSettings(data);
        } catch (err) {
            console.error("Failed to load site settings", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshSiteSettings();
    }, [refreshSiteSettings]);

    return (
        <SiteSettingsContext.Provider value={{ siteSettings, loading, refreshSiteSettings }}>
            {children}
        </SiteSettingsContext.Provider>
    );
}

/**
 * Read the current site settings context.
 * @returns {{siteSettings: Object|null, refreshSiteSettings: Function}}
 */
export function useSiteSettings() {
    return useContext(SiteSettingsContext);
}
